import axios from "axios";
import React, { useEffect, useState } from "react";
import "../css/home.css";
import Constants from "../util/Constants";
import ReactTimeAgo from "react-time-ago";
import styled from "styled-components";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en";
import { Paper, Typography, Box, CircularProgress } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ArticleIcon from "@mui/icons-material/Article";

TimeAgo.addDefaultLocale(en);

const NewsContainer = styled(Paper)`
  padding: 0;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
  box-shadow: 0 4px 20px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.02);
  border: 1px solid rgba(16, 33, 56, 0.06);
  width: 100%;
  overflow: hidden;
`;

const NewsHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, rgba(212, 160, 23, 0.06) 0%, rgba(16, 33, 56, 0.02) 100%);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: 1px solid rgba(16, 33, 56, 0.06);
  &:hover {
    background: linear-gradient(135deg, rgba(212, 160, 23, 0.1) 0%, rgba(16, 33, 56, 0.04) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 33, 56, 0.06);
  }
`;

const NewsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  padding: 1.25rem;
  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    padding: 1.5rem;
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const NewsCard = styled(Box)`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(16, 33, 56, 0.06);
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(16, 33, 56, 0.1);
    border-color: rgba(212, 160, 23, 0.25);
  }
  &:hover .news-title {
    color: #102138;
  }
  &:hover .news-img {
    transform: scale(1.03);
  }
`;

const NewsImageWrapper = styled(Box)`
  width: 100%;
  height: 140px;
  overflow: hidden;
  background: linear-gradient(135deg, #e8eef5 0%, #f0f4f8 100%);
  flex-shrink: 0;
`;

const NewsImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const NewsContent = styled(Box)`
  padding: 1rem 1.25rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const NewsTitle = styled(Typography)`
  font-size: 1rem !important;
  font-weight: 600 !important;
  color: #102138 !important;
  line-height: 1.45 !important;
  margin-bottom: 0.5rem !important;
  display: -webkit-box !important;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-decoration: none;
  transition: color 0.2s ease;
  &:hover {
    color: #d4a017 !important;
  }
`;

const NewsTime = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  color: #5a6c7d;
  font-size: 0.85rem;
  font-weight: 500;
`;

const EmptyState = styled(Box)`
  padding: 3rem 2rem;
  text-align: center;
  color: #5a6c7d;
  .MuiSvgIcon-root {
    font-size: 3rem;
    opacity: 0.4;
    margin-bottom: 1rem;
  }
`;

export default function MainNews(props) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNewsOpen, setIsNewsOpen] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const newsResponse = await axios(Constants.NEWS_URL);
        setNews(newsResponse.data.slice(0, 15));
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  return (
    <NewsContainer elevation={0}>
      {!props.isNewsOpen && (
        <NewsHeader onClick={() => setIsNewsOpen(!isNewsOpen)}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #d4a017 0%, #e8c547 100%)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.25rem",
            }}
          >
            +
          </Box>
          <Typography sx={{ fontWeight: 600, color: "#102138", fontSize: "1.1rem" }}>
            Latest News
          </Typography>
        </NewsHeader>
      )}

      {(isNewsOpen || props.isNewsOpen) && (
        <Box sx={{ padding: { xs: "1rem", sm: "1.5rem" } }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: "#d4a017" }} size={36} />
            </Box>
          ) : news.length === 0 ? (
            <EmptyState>
              <ScheduleIcon />
              <Typography sx={{ fontSize: "1rem", color: "#5a6c7d" }}>
                No news available at the moment.
              </Typography>
            </EmptyState>
          ) : (
            <NewsGrid>
              {news.map((item) => (
                <NewsCard key={item.uuid}>
                  <NewsImageWrapper>
                    {item.image ? (
                      <NewsImg
                        className="news-img"
                        src={item.image}
                        alt=""
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#8b9cad",
                          fontSize: "2rem",
                        }}
                      >
                        <ArticleIcon sx={{ fontSize: 40 }} />
                      </Box>
                    )}
                  </NewsImageWrapper>
                  <NewsContent>
                    <NewsTitle
                      className="news-title"
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.title}
                    </NewsTitle>
                    <NewsTime>
                      <ScheduleIcon sx={{ fontSize: 16, color: "#8b9cad" }} />
                      <ReactTimeAgo date={item.publishedDate} locale="en-US" />
                    </NewsTime>
                  </NewsContent>
                </NewsCard>
              ))}
            </NewsGrid>
          )}
        </Box>
      )}
    </NewsContainer>
  );
}
